import React from "react";
import {
  Grid,
  Stepper,
  Step,
  StepButton,
  StepLabel,
  Typography,
  LinearProgress,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import colors from "../../Constants/colors";
import theme from "../../Constants/theme";

const QontoConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: "calc(-50% + 16px)",
    right: "calc(50% + 16px)",
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: colors.accent,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: colors.accent,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: colors.gray200,
    borderTopWidth: 3,
    borderRadius: 1,
  },
}));

interface StepperComponentProps {
  currentStep: number;
  steps: string[];
  onStepClick?: (index: number) => void;
  isStepCompleted?: (index: number) => boolean;
  isStepAccessible?: (index: number) => boolean;
  getStepLabel?: (index: number) => string | null;
  onPreviousStep?: () => void;
  onNextStep?: () => void;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
}

const StepperComponent: React.FC<StepperComponentProps> = ({
  currentStep,
  steps,
  onStepClick,
  isStepCompleted,
  isStepAccessible,
  getStepLabel,
  onPreviousStep,
  onNextStep,
  canGoPrevious = false,
  canGoNext = false,
}) => {
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallScreen = useMediaQuery("(max-width: 380px)");
  const isExtraSmallScreen = useMediaQuery("(max-width: 300px)");

  return (
    <Grid
      container
      justifyContent="center"
      direction="column"
      alignItems="center"
    >
      <Grid
        item
        sx={{ width: "100%", marginTop: "1rem", marginBottom: "1rem" }}
      >
        <LinearProgress
          variant="determinate"
          value={(currentStep / (steps.length - 1)) * 100}
          sx={{
            height: 5,
            borderRadius: 5,
            backgroundColor: colors.gray200,
            "& .MuiLinearProgress-bar": {
              backgroundColor: colors.accent,
            },
          }}
        />
      </Grid>
      <Grid
        container
        justifyContent="center"
        direction="row"
        alignItems="center"
        sx={{ width: "100%" }}
      >
        {onPreviousStep && (
          <IconButton
            onClick={onPreviousStep}
            disabled={!canGoPrevious}
            sx={{
              color: canGoPrevious ? colors.primary_dark : colors.gray400,
              marginRight: 1,
              "& svg": {
                marginLeft: "5px",
              },
              "&:hover": {
                backgroundColor: canGoPrevious ? colors.gray100 : "transparent",
              },
              "&.Mui-disabled": {
                color: colors.gray400,
              },
            }}
          >
            <ArrowBackIosIcon />
          </IconButton>
        )}
        <Stepper
          activeStep={currentStep}
          alternativeLabel
          nonLinear
          connector={<QontoConnector />}
          sx={{
            width: isMobile ? "90%" : "auto",
            flex: 1,
            flexWrap: "wrap",
            "& .MuiStep-root": {
              flex: 1,
              minWidth: 0,
            },
            "& .MuiStepIcon-root": {
              color: colors.gray300,
            },
            "& .MuiStepIcon-root.Mui-completed": {
              color: colors.accent,
            },
            "& .MuiStepIcon-root.Mui-active": {
              color: colors.accent,
            },
            "& .MuiStepIcon-text": {
              fill: colors.gray600,
              fontWeight: 600,
              fontSize: "0.75rem",
            },
            "& .MuiStepIcon-root.Mui-active .MuiStepIcon-text": {
              fill: colors.white,
            },
            "& .MuiStepIcon-root.Mui-completed .MuiStepIcon-text": {
              fill: colors.white,
            },
            "& .MuiStepButton-root:not(.Mui-disabled)": {
              "&:hover": {
                "& .MuiStepLabel-label": {
                  color: colors.primary_dark,
                },
              },
            },
          }}
        >
          {steps.map((step, index) => {
            const isCompleted = isStepCompleted
              ? isStepCompleted(index)
              : index < currentStep;
            // A step is clickable if it's accessible (all previous steps completed) or is the current step
            const accessible = isStepAccessible
              ? isStepAccessible(index)
              : index <= currentStep;
            const isClickable =
              (accessible || index === currentStep) && !!onStepClick;
            const selectedLabel = getStepLabel ? getStepLabel(index) : null;
            const displayText = selectedLabel || step;

            // Truncate long text
            const truncateText = (text: string, maxLength: number = 15) => {
              if (text.length <= maxLength) return text;
              return text.substring(0, maxLength) + "...";
            };

            return (
              <Step key={index} completed={isCompleted}>
                <StepButton
                  onClick={isClickable ? () => onStepClick?.(index) : undefined}
                  disabled={!isClickable}
                  sx={{
                    cursor: isClickable ? "pointer" : "default",
                    "&.Mui-disabled": {
                      cursor: "default",
                    },
                  }}
                >
                  <StepLabel>
                    <Typography
                      sx={{
                        fontSize: isExtraSmallScreen
                          ? "0.5rem"
                          : isSmallScreen
                            ? "0.6rem"
                            : isMobile
                              ? "0.75rem"
                              : "0.9rem",
                        color: selectedLabel
                          ? colors.primary_dark
                          : colors.gray700,
                        fontWeight: selectedLabel ? 600 : 400,
                        lineHeight: isExtraSmallScreen
                          ? "1.1"
                          : isSmallScreen
                            ? "1.2"
                            : isMobile
                              ? "1.3"
                              : "1.4",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        textAlign: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                      title={displayText}
                    >
                      {selectedLabel ? (
                        truncateText(displayText, isMobile ? 12 : 18)
                      ) : step === "Select Question List" ? (
                        <>
                          <span>Select Question</span>
                          <br />
                          <span>List</span>
                        </>
                      ) : (
                        step
                      )}
                    </Typography>
                  </StepLabel>
                </StepButton>
              </Step>
            );
          })}
        </Stepper>
        {onNextStep && (
          <IconButton
            onClick={onNextStep}
            disabled={!canGoNext}
            sx={{
              color: canGoNext ? colors.primary_dark : colors.gray400,
              marginLeft: 1,
              "&:hover": {
                backgroundColor: canGoNext ? colors.gray100 : "transparent",
              },
              "&.Mui-disabled": {
                color: colors.gray400,
              },
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        )}
      </Grid>
    </Grid>
  );
};

export default StepperComponent;
